<template>
    <div class="ds-calendar">
        <div v-if="title" class="titlebar">
            <div>{{ title.text || title }}</div>
            <button v-if="!title.static" class="btn-titlebar" @click="$emit('close')">
                <DSFontIcon
                    code="close"
                    icon-style="line"
                    icon-set="st1website"
                />
            </button>
        </div>
        <div class="calendar" :class="{ 'single-select': !rangeSelect, 'range-select': rangeSelect }">
            <div class="head">
                <button
                    :aria-label="header.prev.ariaLabel"
                    :disabled="header.prev.disabled"
                    @click="header.prev.onClick"
                >
                    <DSFontIcon code="chevron-left" icon-style="line" />
                </button>
                <button class="period" @click="view = view === 'month' ? 'year' : 'month'">
                    <TransitionGroup :name="transitionName">
                        <template v-for="i in transitionUpdater" :key="i">
                            <span v-if="view === 'day'">
                                {{ date.setLocale($dsc.locale.code).monthLong }} {{
                                    date.setLocale($dsc.locale.code).toFormat('yyyy')
                                }}
                            </span>
                            <span v-if="view === 'month'">{{ date.year }}</span>
                            <span v-if="view === 'year'">{{ visibleYears[0] }} - {{ visibleYears.at(-1) }}</span>
                        </template>
                    </TransitionGroup>
                </button>
                <button
                    :aria-label="header.next.ariaLabel"
                    :disabled="header.next.disabled"
                    @click="header.next.onClick"
                >
                    <DSFontIcon code="chevron-right" icon-style="line" />
                </button>
            </div>

            <Transition name="blink">
                <div v-if="view === 'day'" class="weekdays">
                    <div v-for="weekday in Info.weekdays('short', { locale: $dsc.locale.code })" :key="weekday">
                        {{ weekday }}
                    </div>
                </div>
            </Transition>

            <TransitionGroup :name="transitionName">
                <template v-for="i in transitionUpdater" :key="i">
                    <div v-if="view === 'day'" class="days">
                        <button
                            v-for="day in calendarDays"
                            :key="day"
                            type="button"
                            class="day"
                            :class="{
                                today: day.ts === DateTime.now().startOf('day').ts,
                                'another-month': day.month !== date.month,
                                'range-start': modelValue && rangeSelect && !editRangeMode && day.ts === range.start.startOf('day').ts,
                                selected: modelValue && !editRangeMode && selected(day),
                                'picked-for-new-range': rangeSelect && editRangeMode && day.ts === range.start.startOf('day').ts,
                                'range-end': modelValue && rangeSelect && !editRangeMode && day.ts === range.end.startOf('day').ts,
                                ...specialDays(day),
                            }"
                            :aria-selected="selected(day)"
                            :aria-label="day.setLocale($dsc.locale.code).toLocaleString(DateTime.DATE_HUGE)"
                            :disabled="outOfRange(day) || special(day)"
                            @click="setDay(day)"
                        >
                            {{ day.day }}
                        </button>
                    </div>

                    <div v-if="view === 'month'" class="months">
                        <button
                            v-for="month, m in Info.months('short', { locale: $dsc.locale.code })"
                            :key="m"
                            class="month"
                            :class="{
                                selected: modelValue && selected(DateTime.fromObject({ month: m + 1, year: date.year }), 'month'),
                                'range-start': modelValue && rangeSelect && DateTime.fromObject({ month: m + 1, year: date.year }).ts === range.start.startOf('month').ts,
                                'range-end': modelValue && rangeSelect && DateTime.fromObject({ month: m + 1, year: date.year }).ts === range.end.startOf('month').ts,
                            }"
                            :disabled="periodIsOutOfRange(m + 1, date.year, 'month')"
                            :aria-label="DateTime.fromObject({ month: m + 1, year: date.year }).setLocale($dsc.locale.code).monthLong"
                            @click="setMonth(m + 1); updateDays();"
                        >
                            {{ month }}
                        </button>
                    </div>

                    <div v-if="view === 'year'" class="years">
                        <button
                            v-for="year in visibleYears"
                            :key="year"
                            class="year"
                            :class="{
                                selected: modelValue && selected(DateTime.fromObject({ year }), 'year'),
                                'range-start': modelValue && rangeSelect && DateTime.fromObject({ year }).ts === range.start.startOf('year').ts,
                                'range-end': modelValue && rangeSelect && DateTime.fromObject({ year }).ts === range.end.startOf('year').ts,
                            }"
                            :disabled="periodIsOutOfRange(1, year, 'year')"
                            :aria-label="year"
                            @click="setYear(year)"
                        >
                            {{ year }}
                        </button>
                    </div>
                </template>
            </TransitionGroup>
        </div>
        <slot name="footer">
            <div v-if="controls" class="controls">
                <button
                    class="ds-btn ds-btn--primary ds-btn--large"
                    :disabled="modelValue === null"
                    @click="clearSelection"
                >{{ $t('dsCalendar.clear') }}
                </button>
                <button
                    class="ds-btn ds-btn--primary ds-btn--large"
                    :disabled="disabledTodayBtn"
                    @click="moveToCurrentMonth"
                >{{ $t('dsCalendar.today') }}
                </button>
            </div>
        </slot>
    </div>
</template>
<script setup>
import { DateTime, Interval, Info } from 'luxon';
import { ref, computed, watch, useNuxtApp } from '#imports';

const { $i18n, $dsc } = useNuxtApp();

const props = defineProps({
    modelValue: {
        type: [null, Object],
        required: true,
        validator: v => {
            const validType = v?.isLuxonDateTime || v?.isLuxonInterval || v === null;

            const validRangeStartDate = v?.isLuxonInterval ? v.start.startOf('day').ts === v.start.ts : true;
            const validRangeEndDate = v?.isLuxonInterval ? v.end.endOf('day').ts === v.end.ts : true;

            if (!validType) console.warn('[DSCalendar]: luxon DateTime/Interval or null expected');
            if (!validRangeStartDate) console.warn('[DSCalendar]: range start date is expected to be exactly start of the day');
            if (!validRangeEndDate) console.warn('[DSCalendar]: range end date is expected to be exactly end of the day');

            return validType && validRangeStartDate && validRangeEndDate;
        },
    },
    rangeSelect: {
        type: Boolean,
        default: p => !!p.modelValue?.isLuxonInterval,
    },
    minDate: {
        type: Object,
        default: null,
        validator: v => v?.isLuxonDateTime,
    },
    maxDate: {
        type: Object,
        default: null,
        validator: v => v?.isLuxonDateTime,
    },
    specialDates: {
        type: Array,
        default: () => [],
    },
    title: {
        type: [String, Boolean, Object],
        default: false,
    },
    controls: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'close']);

const DAYS_IN_WEEK = 7;
const MONTHS_IN_YEAR = 12;
const AMOUNT_OF_VISIBLE_YEARS = 12;

const date = ref((props.rangeSelect
    ? props.modelValue?.start
    : props.modelValue) || DateTime.now().startOf('day'));

const range = ref(props.rangeSelect ? (props.modelValue || Interval.fromDateTimes(DateTime.now()
    .startOf('day'), DateTime.now().endOf('day'))) : null);
const editRangeMode = ref(false);
const view = ref('day');
const transitionName = ref('');
const transitionUpdater = ref([DateTime.now().ts]);

const updateTransition = sign => {
    transitionUpdater.value = [DateTime.now().ts];
    transitionName.value = `slide-${sign}`;
};

watch(view, () => transitionName.value = 'blink');

const calendarDays = ref(undefined);

const updateDays = () => {
    const displayedMonth = {
        firstWeekday: date.value.startOf('month').weekday,
        lastWeekday: date.value.endOf('month').weekday,
    };

    const intervalStart = date.value.startOf('month').minus({ day: displayedMonth.firstWeekday - 1 });
    const intervalEnd = date.value.endOf('month').plus({ day: DAYS_IN_WEEK - displayedMonth.lastWeekday });

    calendarDays.value = Interval
        .fromDateTimes(intervalStart, intervalEnd)
        .splitBy({ days: 1 })
        .map(day => day.start);
};

updateDays();

const navPeriod = (sign = 'plus', period = 'month') => {
    date.value = date.value[sign]({ [period]: 1 });
    if (period === 'month') updateDays();
    updateTransition(sign);
};

const navVisibleYears = sign => {
    date.value = date.value[sign]({ month: AMOUNT_OF_VISIBLE_YEARS * MONTHS_IN_YEAR });
    updateTransition(sign);
};

const visibleYears = computed(() => {
    const intervalBaseYear = date.value.year % AMOUNT_OF_VISIBLE_YEARS;
    const intervalStart = date.value.startOf('year').minus({ year: intervalBaseYear });
    const intervalEnd = date.value.endOf('year').plus({ year: AMOUNT_OF_VISIBLE_YEARS - intervalBaseYear });

    return [...Array(intervalEnd.year - intervalStart.year)].map((_, i) => intervalStart.year + i);
});

const header = computed(() => {
    switch (view.value) {
        case 'month':
            return {
                prev: {
                    ariaLabel: $i18n.t('dsCalendar.prevYear'),
                    disabled: date.value.year <= props.minDate?.year,
                    onClick: () => navPeriod('minus', 'year'),
                },
                next: {
                    ariaLabel: $i18n.t('dsCalendar.nextYear'),
                    disabled: date.value.year >= props.maxDate?.year,
                    onClick: () => navPeriod('plus', 'year'),
                },
            };
        case 'year':
            return {
                prev: {
                    ariaLabel: $i18n.t('dsCalendar.prevYear', { period: AMOUNT_OF_VISIBLE_YEARS }, AMOUNT_OF_VISIBLE_YEARS),
                    disabled: visibleYears.value[0] <= props.minDate?.year,
                    onClick: () => navVisibleYears('minus'),
                },
                next: {
                    ariaLabel: $i18n.t('dsCalendar.nextYear', { period: AMOUNT_OF_VISIBLE_YEARS }, AMOUNT_OF_VISIBLE_YEARS),
                    disabled: visibleYears.value.at(-1) >= props.maxDate?.year,
                    onClick: () => navVisibleYears('plus'),
                },
            };
        default:
            return {
                prev: {
                    ariaLabel: $i18n.t('dsCalendar.prevMonth'),
                    disabled: date.value.startOf('month') <= props.minDate?.startOf('month'),
                    onClick: () => navPeriod('minus', 'month'),
                },
                next: {
                    ariaLabel: $i18n.t('dsCalendar.nextMonth'),
                    disabled: date.value.endOf('month') >= props.maxDate?.endOf('month'),
                    onClick: () => navPeriod('plus', 'month'),
                },
            };
    }
});

let startDate;
let endDate;

const setDay = day => {
    date.value = day.startOf('day');

    if (!props.rangeSelect) return emit('update:modelValue', date.value);

    if (!editRangeMode.value) {
        startDate = day.startOf('day');
        endDate = startDate.endOf('day');
    } else {
        endDate = day.endOf('day');

        if (startDate > endDate) [endDate, startDate] = [startDate.endOf('day'), endDate.startOf('day')];

        emit('update:modelValue', Interval.fromDateTimes(startDate, endDate));
    }

    range.value = Interval.fromDateTimes(startDate, endDate);
    editRangeMode.value = !editRangeMode.value;
};

const selected = (d, period = 'day') => {
    if (props.rangeSelect) return Interval.fromDateTimes(d.startOf(period), d.endOf(period)).overlaps(range.value);

    return props.modelValue?.startOf(period).ts === d?.startOf(period).ts;
};

const outOfRange = day => (day.ts < props.minDate?.ts) || (day.ts > props.maxDate?.ts);
const periodIsOutOfRange = (month, year, period = 'month') => {
    const firstDayOfPeriod = DateTime.fromFormat(`1.${month}.${year}`, 'd.M.yyyy');

    if (!props.rangeSelect && date.value.startOf(period).ts === firstDayOfPeriod.ts) return false;

    if (props.rangeSelect) {
        const lastDayOfPeriod = firstDayOfPeriod.endOf(period);

        if (Interval.fromDateTimes(firstDayOfPeriod, lastDayOfPeriod).overlaps(range.value)) return false;
    }

    return outOfRange(firstDayOfPeriod);
};

const special = (day, dayType = 'disabled') => props.specialDates.some(({ type, days, repeat }) => {
    if (type !== dayType) return false;

    return days?.some(d => d.startOf('day').ts === day.ts)
        || repeat?.weekDays.includes(day.setLocale($dsc.locale.code).localWeekday);
});

const specialDays = day => props.specialDates.reduce((acc, { type }, i) => {
    acc[`${type}-day` || `selected-day-${i}`] = special(day, type);

    return acc;
}, {});

const setPeriod = (value, period = 'month', nextView = 'day') => {
    date.value = date.value.set({ [period]: value }).startOf(period);
    view.value = nextView;
};

const setMonth = month => setPeriod(month);
const setYear = year => setPeriod(year, 'year', 'month');

const disabledTodayBtn = computed(() => {
    const { month: currentMonth } = DateTime.now().startOf('day');

    return view.value === 'day' && currentMonth === date.value.month;
});

const moveToCurrentMonth = () => {
    const { month: currentMonth, year: currentYear } = DateTime.now().startOf('day');

    date.value = date.value.set({ month: currentMonth, year: currentYear }).startOf('month');
    view.value = 'day';
    updateTransition(currentMonth > date.value.month ? 'plus' : 'minus');
    updateDays();
};

const clearSelection = () => {
    if (!editRangeMode.value) editRangeMode.value = false;
    if (props.modelValue !== null) emit('update:modelValue', null);
};

defineExpose({
    range,
    editRangeMode,
    clearSelection,
    moveToCurrentMonth,
    setDay,
    setMonth,
    setYear,
});
</script>

<style scoped>
.ds-calendar {
  background-color: var(--color-base-white-default);
  border: 1px solid var(--color-base-grey-200);
  border-radius: 0.25rem;
  box-shadow: 0 2px 0.25rem var(--color-base-black-mine-shaft-opaque);
  font-weight: 700;
  width: fit-content;
}

.titlebar {
  display: grid;
  border-bottom: 1px solid var(--color-base-grey-200);
  grid-template-columns: 1fr auto;
  padding: 0.75rem 1rem;
  align-items: center;
  font-size: 1.125rem;
  font-weight: 800;
}

.calendar {
  display: grid;
  grid-template-columns: repeat(7, minmax(2.5rem, auto));
  border: 1rem solid var(--color-base-white-default);
  overflow: hidden;
}
.calendar > * {
  display: grid;
  grid-column: 1/-1;
}
.calendar .head {
  display: grid;
  grid-template-columns: auto 1fr auto;
  column-gap: 1rem;
  padding-block: 0.25rem;
  padding-bottom: 1rem;
  place-items: center;
}
.calendar .head .period {
  grid-row: 1;
  grid-column: 2;
  overflow: hidden;
  display: grid;
  justify-self: stretch;
}
.calendar .head .period > * {
  grid-column: 1;
  grid-row: 1;
}
.calendar .weekdays,
.calendar .days {
  grid-template-columns: subgrid;
  grid-auto-rows: 2.5rem;
  place-items: center;
}
.calendar .weekdays {
  color: var(--color-base-grey-400);
}
.calendar .months,
.calendar .years {
  grid-auto-rows: 4.5rem;
}
.calendar .days,
.calendar .months,
.calendar .years {
  row-gap: 0.25rem;
  grid-row: 3;
}
.calendar .days .day,
.calendar .days .month,
.calendar .days .year,
.calendar .months .day,
.calendar .months .month,
.calendar .months .year,
.calendar .years .day,
.calendar .years .month,
.calendar .years .year {
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
}
.calendar .days .day:hover:not(.selected),
.calendar .days .month:hover:not(.selected),
.calendar .days .year:hover:not(.selected),
.calendar .months .day:hover:not(.selected),
.calendar .months .month:hover:not(.selected),
.calendar .months .year:hover:not(.selected),
.calendar .years .day:hover:not(.selected),
.calendar .years .month:hover:not(.selected),
.calendar .years .year:hover:not(.selected) {
  background-color: var(--color-base-grey-100);
}
.calendar .days .day.selected[disabled],
.calendar .months .day.selected[disabled],
.calendar .years .day.selected[disabled] {
  background-color: var(--color-primary-200);
}
.calendar .days .day.another-month, .calendar .days .day.another-month.selected,
.calendar .months .day.another-month,
.calendar .months .day.another-month.selected,
.calendar .years .day.another-month,
.calendar .years .day.another-month.selected {
  cursor: default;
  opacity: 0;
  pointer-events: none;
}
.calendar .days .day.another-month:hover, .calendar .days .day.another-month.selected:hover,
.calendar .months .day.another-month:hover,
.calendar .months .day.another-month.selected:hover,
.calendar .years .day.another-month:hover,
.calendar .years .day.another-month.selected:hover {
  background: transparent;
}
.calendar .days .today:not(.selected),
.calendar .months .today:not(.selected),
.calendar .years .today:not(.selected) {
  border: 2px solid var(--color-primary-700);
}
.calendar .days .today:not(.selected)[disabled],
.calendar .months .today:not(.selected)[disabled],
.calendar .years .today:not(.selected)[disabled] {
  border: 2px solid var(--color-primary-200);
}
.calendar .months {
  grid-template-columns: repeat(4, 1fr);
}
.calendar .years {
  grid-template-columns: repeat(4, 1fr);
}
.calendar button[disabled] {
  cursor: not-allowed;
  pointer-events: all;
  color: var(--color-base-grey-500);
}

.calendar.single-select .day.selected,
.calendar.single-select .month.selected,
.calendar.single-select .year.selected {
  color: var(--color-base-white);
  background-color: var(--color-primary-700);
}

.calendar.range-select .picked-for-new-range {
  border: 2px solid var(--color-primary-300);
  background-color: var(--color-primary-100);
}
.calendar.range-select .selected:not(.picked-for-new-range) {
  border-radius: 0;
  background-color: var(--color-primary-200);
}
.calendar.range-select .selected:not(.picked-for-new-range).range-start, .calendar.range-select .selected:not(.picked-for-new-range).range-end {
  color: var(--color-base-white-default);
  background: radial-gradient(at center, var(--color-primary-700) 0%, var(--color-primary-700) 70%, var(--color-primary-200) 71%);
}
.calendar.range-select .selected:not(.picked-for-new-range).range-start {
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
}
.calendar.range-select .selected:not(.picked-for-new-range).range-end {
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
}
.calendar.range-select .selected.day:not(.range-start).selected:nth-child(7n+1), .calendar.range-select .selected.month:not(.range-start).selected:nth-child(4n+1), .calendar.range-select .selected.year:not(.range-start).selected:nth-child(4n+1) {
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
}
.calendar.range-select .selected.day:not(.range-end).selected:nth-child(7n), .calendar.range-select .selected.month:not(.range-end).selected:nth-child(4n), .calendar.range-select .selected.year:not(.range-end).selected:nth-child(4n) {
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}

.controls {
  border-top: 1px solid var(--color-base-grey-200);
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 1rem;
  gap: 1rem;
}

.slide-plus-enter-active,
.slide-plus-leave-active,
.slide-minus-enter-active,
.slide-minus-leave-active {
  opacity: 1;
  transition: all 0.25s linear;
}

.slide-plus-enter-from,
.slide-plus-leave-to,
.slide-minus-enter-from,
.slide-minus-leave-to {
  opacity: 0;
  filter: blur(0.25rem);
}

.slide-plus-enter-from,
.slide-minus-leave-to {
  transform: translateX(100%);
}

.slide-plus-leave-to,
.slide-minus-enter-from {
  transform: translateX(-100%);
}
</style>
